import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, CodeBlock, BlockType, ValidationMessage, ValidationMessageType, SettingState,
} from '@appblocks/codegeneration/types';
import {
    RESERVED_KEYWORDS,
} from '@appblocks/platforms/PlatformUtils';
import * as simulator from './simulator';
import * as tios from './tios';

export interface TableFieldLegacy {
    DEFVAL: string,
    FIELD_NAME: string,
    FIELD_TYPE: string,
    FLD_COMMENT: string,
    length: number,
    P1: string,
    P2: string,
}

export default class TableField extends BaseFeature {
    constructor() {
        super();
        this.name = 'Table Field';
        this.id = 'tbl_field';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'NAME',
                name: 'Field ID',
                settingType: SettingType.STRING,
                required: true,
                default: 'F',
                min: 1,
                max: 32,
            },
            {
                id: 'DISPLAY_NAME',
                name: 'Display Name',
                settingType: SettingType.STRING,
                min: 0,
                required: true,
                default: '',
            },
            {
                id: 'FIELDTYPE',
                name: 'Field Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'S',
                options: [
                    {
                        name: 'String(text)',
                        value: 'S',
                    },
                    {
                        name: 'Number(value)',
                        value: 'F',
                    },
                    {
                        name: 'DateTime',
                        value: 'E',
                    },
                    {
                        name: 'Time',
                        value: 'M',
                    },
                ],
            },
            {
                id: 'P1',
                name: 'Minimum',
                settingType: SettingType.NUMBER,
                required: true,
                default: '0',
            },
            {
                id: 'P2',
                name: 'Maximum',
                settingType: SettingType.NUMBER,
                required: true,
                default: '50',
            },
            {
                id: 'DEFAULT',
                name: 'Default Value',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
            {
                id: 'ENUMS',
                name: 'Options',
                settingType: SettingType.ARRAY,
                showChildren: true,
                required: false,
                refId: 'luis_item_option',
                default: [],
            },
        ]);
    }

    initSettings(project?: any, parentFeature?: any): void {
        super.initSettings(project, parentFeature, 'NAME');
    }

    getDependencies() {
        const dependencies = [
            'tbl',
        ];
        return dependencies;
    }

    getState(settingId: string): SettingState {
        const fieldType = this.getSettingValue('FIELDTYPE');
        const parent = this.project.getParent(this);
        const FIELDTYPE = this.getSettingValue('FIELDTYPE');
        if (settingId === 'ENUMS') {
            if (FIELDTYPE === 'E' || FIELDTYPE === 'M') {
                return SettingState.HIDDEN;
            }
            return SettingState.ENABLED;
        }
        if (parent) {
            const parentNAME = parent.getSettingValue('NAME');
            const parentTableType = parent.getSettingValue('STRUCT');
            if (parentTableType === 'L' && (parentNAME === 'LOG' || parentNAME === 'DEBUG')) {
                return SettingState.DISABLED;
            }
            switch (settingId) {
                case 'P1':
                case 'P2':
                    if (fieldType === 'E' || fieldType === 'M') {
                        return SettingState.HIDDEN;
                    }
                    break;
                default:

                    break;
            }
            return SettingState.ENABLED;
        }
        return super.getState(settingId);
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const name = this.getSettingValue('NAME').trim();
        const displayName = this.getSettingValue('DISPLAY_NAME').trim();
        const ID = this.getID();
        const FIELDTYPE = this.getSettingValue('FIELDTYPE');
        const minVal = this.getSettingValue('P1');
        const maxVal = this.getSettingValue('P2');
        const minValNum = Number(minVal);
        const maxValNum = Number(maxVal);
        const NumberMin = -2147483648;
        const NumberMax = 2147483647;
        const DEFAULT = this.getSettingValue('DEFAULT');
        if (FIELDTYPE === 'E') {
            if (minVal !== '0') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'P1',
                    data: '0',
                });
            }
            if (maxVal !== '2147483647') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'P2',
                    data: '2147483647',
                });
            }
        }
        if (FIELDTYPE !== 'S') {
            if (!/^[0-9]*$/.test(this.getSettingValue('DEFAULT'))) {
                errors.push({
                    message: 'Invalid default field value',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'DEFAULT',
                });
            }
        }
        if (FIELDTYPE === 'S') {
            // string
            // eslint-disable-next-line no-restricted-globals
            if (minValNum < 0 || Number.isNaN(minValNum) || minVal === '') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'P1',
                    data: '0',
                });
            }
            if (maxValNum > 128 || Number.isNaN(maxValNum) || maxVal === '') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'P2',
                    data: '50',
                });
            }
            if (maxVal !== '' && DEFAULT.length > maxValNum) {
                errors.push({
                    message: 'Default string length must be less than Maximum Parameter',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'DEFAULT',
                });
            }
            if (minVal !== '' && DEFAULT.length < minValNum) {
                errors.push({
                    message: 'Default string length must be greater than Minimum Parameter',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'DEFAULT',
                });
            }
        }
        if (FIELDTYPE === 'F') {
            // string
            // eslint-disable-next-line no-restricted-globals
            if (minValNum < NumberMin || Number.isNaN(minValNum) || minVal === '') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'P1',
                    data: NumberMin.toString(),
                });
            }
            if (maxValNum > NumberMax || Number.isNaN(maxValNum) || maxVal === '') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'P2',
                    data: NumberMax.toString(),
                });
            }
            if (Number.isNaN(DEFAULT)) {
                errors.push({
                    message: 'Default value must be a number',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'DEFAULT',
                });
            } else {
                if (minVal !== '' && Number(DEFAULT) < minValNum) {
                    errors.push({
                        message: 'Default value must be greater than Minimum Parameter',
                        messageType: ValidationMessageType.ERROR,
                        featureId: ID,
                        settingId: 'DEFAULT',
                    });
                }
                if (maxVal !== '' && Number(DEFAULT) > maxValNum) {
                    errors.push({
                        message: 'Default value must be less than Maximum Parameter',
                        messageType: ValidationMessageType.ERROR,
                        featureId: ID,
                        settingId: 'DEFAULT',
                    });
                }
            }
        }
        if (RESERVED_KEYWORDS[this.project.runtime].includes(name)) {
            errors.push({
                message: 'Field ID cannot be a reserved keyword',
                messageType: ValidationMessageType.ERROR,
                featureId: ID,
                settingId: 'NAME',
            });
        }
        if (/^\d/.test(name)) {
            errors.push({
                message: 'Field ID cannot start with a number',
                messageType: ValidationMessageType.ERROR,
                featureId: ID,
                settingId: 'NAME',
            });
        }
        const illegalValues = ['#', '==', '>>'];
        if (illegalValues.includes(displayName)) {
            errors.push({
                message: `Display Name cannot contain ${illegalValues.join(', ')}.`,
                messageType: ValidationMessageType.ERROR,
                featureId: ID,
                settingId: 'DISPLAY_NAME',
            });
        }
        if (illegalValues.includes(DEFAULT)) {
            errors.push({
                message: `Default Value cannot contain ${illegalValues.join(', ')}.`,
                messageType: ValidationMessageType.ERROR,
                featureId: ID,
                settingId: 'DEFAULT',
            });
        }
        if (maxValNum !== undefined && minValNum > maxValNum && FIELDTYPE !== 'E' && FIELDTYPE !== 'M') {
            errors.push({
                featureId: ID,
                settingId: 'FIELDS',
                message: 'Minimum parameter cannot be greater than maximum parameter',
                messageType: ValidationMessageType.ERROR,
            });
        }
        return errors;
    }

    getCodeBlocks(): Array<CodeBlock> {
        switch (this.project.runtime) {
            case 'simulator':
                return simulator.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            default:
                return [];
        }
    }
}
